// File: frontend/src/pages/LandingPage.jsx

import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';

export default function LandingPage() {
  const { currentUser, logout } = useAuth();

  const isStaff = currentUser && ['admin', 'teacher', 'accountant'].includes(currentUser.role);
  const isStudent = currentUser && currentUser.role === 'student';

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans text-slate-800 selection:bg-emerald-500 selection:text-white">
      {/* 1. Header / Navbar */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img 
              src="/logo192.png" 
              alt="Irie Roots Logo" 
              className="w-10 h-10 rounded-xl object-cover shadow-md shadow-emerald-500/20 border border-emerald-500/20" 
            />
            <div>
              <span className="font-extrabold text-lg text-slate-900 tracking-tight block leading-tight">
                Irie Roots <span className="text-emerald-600">Attendance</span>
              </span>
              <span className="text-[11px] font-medium text-slate-400 block tracking-wider uppercase">
                愛瑞美語教育管理平台
              </span>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {currentUser ? (
              <div className="flex items-center space-x-3">
                <span className="text-xs text-slate-500 hidden sm:inline">
                  Signed in as <strong className="text-slate-800">{currentUser.username || currentUser.name}</strong>
                </span>
                <Link
                  to={isStaff ? '/dashboard' : '/student/dashboard'}
                  className="inline-flex items-center px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs sm:text-sm font-semibold rounded-lg shadow-sm shadow-emerald-600/30 transition-all"
                >
                  Go to Dashboard &rarr;
                </Link>
                <button
                  onClick={logout}
                  className="px-3 py-2 text-xs text-slate-500 hover:text-slate-700 font-medium"
                >
                  Logout
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-2 sm:space-x-3">
                <Link
                  to="/student-login"
                  className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold text-slate-700 hover:text-emerald-600 border border-slate-300 hover:border-emerald-500 rounded-lg transition-all"
                >
                  Student Portal (學生專區)
                </Link>
                <Link
                  to="/login"
                  className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm shadow-blue-600/30 transition-all"
                >
                  Staff Login (教師登入)
                </Link>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* 2. Hero Section */}
      <section className="relative overflow-hidden pt-12 pb-16 lg:pt-20 lg:pb-24">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(45rem_50rem_at_top,theme(colors.emerald.100),theme(colors.slate.50))] opacity-60" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100/80 border border-emerald-300 text-emerald-800 text-xs font-semibold uppercase tracking-wider mb-6">
            <span>✨ Taiwan ESL Academy System</span>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse"></span>
            <span>Live Attendance & Tuition</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 tracking-tight leading-[1.15] mb-6">
            Empowering Language Education with{' '}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-600 via-teal-600 to-blue-600">
              Smart Attendance
            </span>
          </h1>

          <p className="text-base sm:text-lg lg:text-xl text-slate-600 max-w-2xl mx-auto mb-10 leading-relaxed">
            愛瑞美語專用教學出席與管理系統。無縫支援私人家教、安親補習班合作課程、線上請假、課堂講義及多元繳費管理。
          </p>

          {/* Action Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto text-left">
            {/* Card 1: Teachers & Admin */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 shadow-xl shadow-slate-200/50 border border-slate-200/80 flex flex-col justify-between hover:border-blue-300 hover:shadow-blue-500/10 transition-all group">
              <div>
                <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-2xl mb-4 group-hover:scale-110 transition-transform">
                  👨‍🏫
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-xl font-bold text-slate-900">Teacher & Admin Portal</h3>
                  <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-blue-100 text-blue-800">
                    教職員
                  </span>
                </div>
                <p className="text-sm text-slate-500 mb-6">
                  One-click roll call, private tutoring billing, cram school hourly payroll tracking, and student CSV management.
                </p>
                <ul className="text-xs text-slate-600 space-y-2 mb-6">
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-500 font-bold">✓</span> Quick 1-click attendance marking & roll call
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-500 font-bold">✓</span> Private Tutoring (1v1, 1v2, 1v3+) & Cram School wage rates
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-500 font-bold">✓</span> Automated payroll reports & PDF payslip generation
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-500 font-bold">✓</span> Cash ledger & LINE Pay payment confirmations
                  </li>
                </ul>
              </div>

              <Link
                to={currentUser && isStaff ? '/dashboard' : '/login'}
                className="w-full text-center py-3 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm shadow-md shadow-blue-600/20 transition-all flex items-center justify-center gap-2"
              >
                <span>{currentUser && isStaff ? 'Open Staff Dashboard' : 'Staff Sign In (教師登入)'}</span>
                <span>&rarr;</span>
              </Link>
            </div>

            {/* Card 2: Students & Parents */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 shadow-xl shadow-slate-200/50 border border-slate-200/80 flex flex-col justify-between hover:border-emerald-300 hover:shadow-emerald-500/10 transition-all group">
              <div>
                <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-2xl mb-4 group-hover:scale-110 transition-transform">
                  🎒
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-xl font-bold text-slate-900">Student & Parent Portal</h3>
                  <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-100 text-emerald-800">
                    學生 / 家長
                  </span>
                </div>
                <p className="text-sm text-slate-500 mb-6">
                  Real-time arrival status, class materials access, online leave requests, and tuition fee records.
                </p>
                <ul className="text-xs text-slate-600 space-y-2 mb-6">
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-500 font-bold">✓</span> View attendance streak & lesson history
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-500 font-bold">✓</span> Online leave application (線上請假 & 補課額度)
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-500 font-bold">✓</span> Download lesson materials, worksheets & logs
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-500 font-bold">✓</span> Payment details: Cash, Bank Transfer & LINE Pay
                  </li>
                </ul>
              </div>

              <Link
                to={currentUser && isStudent ? '/student/dashboard' : '/student-login'}
                className="w-full text-center py-3 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm shadow-md shadow-emerald-600/20 transition-all flex items-center justify-center gap-2"
              >
                <span>{currentUser && isStudent ? 'Open Student Portal' : 'Student & Parent Sign In (學生登入)'}</span>
                <span>&rarr;</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 3. System Highlights Grid */}
      <section className="py-16 bg-white dark:bg-gray-800 border-y border-slate-200/80">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
              Designed Specifically for Taiwan English Education
            </h2>
            <p className="text-slate-500 text-sm mt-2">
              針對台灣英文家教與補習班教學環境量身定制的完整解決方案
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-slate-50 p-6 rounded-xl border border-slate-100 hover:bg-slate-100/80 transition-colors">
              <div className="text-2xl mb-3">🏠</div>
              <h3 className="font-bold text-slate-900 text-base mb-1">Private Tutoring (私人家教)</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                支援 1v1 ($650)、1v2 ($350/人)、1v3+ ($300/人) 及線上課程 ($600) 費率與自訂價格。
              </p>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl border border-slate-100 hover:bg-slate-100/80 transition-colors">
              <div className="text-2xl mb-3">🏫</div>
              <h3 className="font-bold text-slate-900 text-base mb-1">Cram Schools (補習班合作)</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                合作補習班（如 Joy English、Kid Castle）獨立時薪與班級花名冊，精準核算薪資。
              </p>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl border border-slate-100 hover:bg-slate-100/80 transition-colors">
              <div className="text-2xl mb-3">💳</div>
              <h3 className="font-bold text-slate-900 text-base mb-1">Taiwan Payments (多元支付)</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                現金即時確認收款 (One-click Cash Confirmation)、銀行 ATM 轉帳及 LINE Pay 掃碼支付。
              </p>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl border border-slate-100 hover:bg-slate-100/80 transition-colors">
              <div className="text-2xl mb-3">📝</div>
              <h3 className="font-bold text-slate-900 text-base mb-1">Leave & Makeup (線上請假)</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                家長在線申請請假，老師一鍵核准並自動計入補課額度 (Makeup Credits)。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Footer */}
      <footer className="mt-auto bg-slate-900 text-slate-400 py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center space-x-2">
            <span className="text-lg">🌿</span>
            <span className="font-bold text-white tracking-tight">Irie Roots English Academy</span>
            <span className="text-xs text-slate-500 ml-2">| 愛瑞美語出席系統</span>
          </div>

          <div className="flex items-center space-x-6 text-xs">
            <Link to="/login" className="hover:text-white transition-colors">
              Staff Portal
            </Link>
            <Link to="/student-login" className="hover:text-white transition-colors">
              Student Portal
            </Link>
            <span className="text-slate-600">Taiwan • EST. 2026</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
